import React from "react";
import { Award, AlertTriangle, ArrowRight, BookOpen, ExternalLink } from "lucide-react";

const GRADE_COLOR = {
  A: { bg: "rgba(76,175,80,0.10)", border: "#4CAF50", fg: "#4CAF50", label: "FEED / DPR Grade" },
  B: { bg: "rgba(255,176,0,0.10)", border: "#FFB000", fg: "#FFB000", label: "Feasibility / Conceptual" },
  C: { bg: "rgba(255,138,80,0.10)", border: "#FF8A50", fg: "#FF8A50", label: "Screening / Order-of-Magnitude" },
  D: { bg: "rgba(229,115,115,0.10)", border: "#E57373", fg: "#E57373", label: "Indicative — Use Specialist Tools" },
};

export default function AccuracyCard({ report }) {
  if (!report) return null;
  const grade = (report.accuracy_level || "C").toUpperCase();
  const c = GRADE_COLOR[grade] || GRADE_COLOR.C;
  return (
    <div data-testid="accuracy-card" style={{ border: `1px solid ${c.border}`, background: c.bg, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Award size={18} color={c.fg}/>
        <div>
          <div className="pf-overline" style={{ fontSize: 10, color: c.fg, letterSpacing: 1 }}>ACCURACY · GRADE {grade}</div>
          <div className="pf-mono" style={{ fontSize: 12, color: "var(--pf-text)", marginTop: 2 }}>{c.label}</div>
        </div>
      </div>

      <div style={{ marginBottom: 8 }}>
        <Field label="Calculation">{report.calculation_name}</Field>
        <Field label="Method">{report.method_used}</Field>
        <Field label="Accuracy">{report.accuracy_percentage} · {report.confidence}</Field>
        <Field label="Description"><i>{report.accuracy_description}</i></Field>
      </div>

      {(report.limitations?.length || 0) > 0 && (
        <Block title="LIMITATIONS" icon={AlertTriangle} color="#E2A0A0">
          {report.limitations.map((l, i) => <li key={i}>{l}</li>)}
        </Block>
      )}

      {(report.what_to_do_next?.length || 0) > 0 && (
        <Block title="WHAT TO DO NEXT" icon={ArrowRight} color="#4FC3F7">
          {report.what_to_do_next.map((l, i) => <li key={i}>{l}</li>)}
        </Block>
      )}

      {report.export_for_specialist_tools && (
        <Block title="EXPORT FOR SPECIALIST TOOLS" icon={ExternalLink} color="#FFB000">
          {Object.entries(report.export_for_specialist_tools).map(([k, v]) => (
            <li key={k}><span className="pf-mono" style={{ color: "var(--pf-accent)" }}>{k}:</span> {v}</li>
          ))}
        </Block>
      )}

      {(report.references?.length || 0) > 0 && (
        <Block title="REFERENCES" icon={BookOpen} color="#9E9E9E">
          {report.references.map((r, i) => <li key={i} style={{ fontSize: 9, color: "var(--pf-text-dim)" }}>{r}</li>)}
        </Block>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <span className="pf-mono" style={{ fontSize: 9, color: "var(--pf-text-muted)", letterSpacing: 1, display: "block" }}>{label}</span>
      <span style={{ fontSize: 11, color: "var(--pf-text)" }}>{children}</span>
    </div>
  );
}

function Block({ title, icon: I, color, children }) {
  return (
    <div style={{ marginTop: 10 }}>
      <div className="pf-overline" style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9, color, letterSpacing: 1, marginBottom: 4 }}>
        <I size={11}/>{title}
      </div>
      <ul style={{ margin: 0, padding: "0 0 0 18px", fontSize: 10, color: "var(--pf-text)", lineHeight: 1.5 }}>{children}</ul>
    </div>
  );
}
